import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from "react";
import type { DatasetField, DatasetValue } from "@/api/datasets";
import { SelectField, TextInput } from "@/components/ui";
import { useT } from "@/i18n";
import { framesDatasetMessages } from "@/i18n/messages/frames-dataset";
import { cn } from "@/lib/cn";
import { CELL_OVERLAY, DatasetCellBox } from "./dataset-cell-box";
import { isNumericField } from "./dataset-values";

const NUMBER = /^-?(\d+\.?\d*|\.\d+)$/;

/** A stored UTC time as a `datetime-local` value in the person's timezone. */
function localDateTime(value: DatasetValue): string {
  if (typeof value !== "string" || Number.isNaN(Date.parse(value))) return "";
  const time = new Date(value);
  time.setMinutes(time.getMinutes() - time.getTimezoneOffset());
  return time.toISOString().slice(0, 16);
}

function draftFrom(field: DatasetField, value: DatasetValue): string {
  if (value === null || value === undefined) return "";
  return field.type === "datetime" ? localDateTime(value) : String(value);
}

/** The typed draft as the field's value; `undefined` when it is not valid. */
function valueFrom(
  field: DatasetField,
  draft: string,
): DatasetValue | undefined {
  const text = draft.trim();
  if (!text) return null;
  switch (field.type) {
    case "number":
      return NUMBER.test(text) ? Number(text) : undefined;
    case "decimal":
      return NUMBER.test(text) ? text : undefined;
    case "datetime":
      return Number.isNaN(Date.parse(text))
        ? undefined
        : new Date(text).toISOString();
    default:
      return field.type === "text" ? draft : text;
  }
}

/** Where focus goes after a save: the cell, the cell on its right, or nowhere. */
export type CellMove = "stay" | "next" | "none";

/**
 * The editor of one cell, by its field's type, open over the cell (see
 * `CELL_OVERLAY`): text or a number checked as typed in a box that wraps and
 * grows, a native date or local date-time, or a select's options.
 * Enter and Tab save, Escape cancels; a select saves when picked. Leaving
 * the editor saves a valid draft and drops an invalid one, without taking
 * focus back from where the person went.
 */
export function DatasetCellEditor({
  field,
  value,
  onSave,
  onCancel,
}: {
  field: DatasetField;
  value: DatasetValue;
  onSave: (value: DatasetValue, move: CellMove) => void;
  /** `refocus`: give focus back to the cell (not after a blur). */
  onCancel: (refocus: boolean) => void;
}) {
  const t = useT(framesDatasetMessages);
  const [draft, setDraft] = useState(() => draftFrom(field, value));
  const [invalid, setInvalid] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);
  // Saved or cancelled: the blur that follows must not save again.
  const settled = useRef(false);
  useEffect(() => {
    selectRef.current?.querySelector("button")?.focus();
  }, []);

  if (field.type === "select") {
    return (
      <div
        ref={selectRef}
        className="absolute inset-0 flex items-center px-[inherit]"
        onKeyDown={(e) => {
          if (e.key === "Escape") onCancel(true);
        }}
      >
        <SelectField
          value={typeof value === "string" ? value : ""}
          options={(field.options ?? []).map((option) => ({
            value: option.id,
            label: option.label,
          }))}
          onChange={(picked) => onSave(picked, "stay")}
          ariaLabel={t("editCell", { field: field.name })}
          testId="dataset-cell-editor"
        />
      </div>
    );
  }

  const save = (move: CellMove) => {
    if (settled.current) return;
    const parsed = valueFrom(field, draft);
    if (parsed === undefined) {
      setInvalid(true);
      return;
    }
    settled.current = true;
    onSave(parsed, move);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      save(e.key === "Tab" ? "next" : "stay");
    } else if (e.key === "Escape") {
      e.preventDefault();
      settled.current = true;
      onCancel(true);
    }
  };
  const errorId = `dataset-cell-error-${field.id}`;
  const editorProps = {
    autoFocus: true,
    value: draft,
    onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setDraft(e.target.value);
      setInvalid(false);
    },
    onKeyDown,
    onBlur: () => {
      // Leaving an invalid draft drops it, as Escape would.
      if (valueFrom(field, draft) !== undefined) save("none");
      else if (!settled.current) {
        settled.current = true;
        onCancel(false);
      }
    },
    "aria-label": t("editCell", { field: field.name }),
    "aria-invalid": invalid || undefined,
    "aria-describedby": invalid ? errorId : undefined,
    "data-testid": "dataset-cell-editor",
  };
  const isDate = field.type === "date" || field.type === "datetime";
  return (
    <>
      {isDate ? (
        <TextInput
          {...editorProps}
          type={field.type === "date" ? "date" : "datetime-local"}
          className={cn(CELL_OVERLAY, "h-9")}
        />
      ) : (
        <DatasetCellBox
          {...editorProps}
          inputMode={isNumericField(field.type) ? "decimal" : undefined}
        />
      )}
      {invalid && (
        <p
          id={errorId}
          className="absolute left-0 top-full z-10 mt-1 whitespace-nowrap px-[inherit] text-appkit-mini text-danger"
        >
          {t("mustBeNumber")}
        </p>
      )}
    </>
  );
}
